import { useState, useEffect, useRef } from "react";
import { B, HD, PD } from "../theme";
import { StarField } from "../shared/StarField";
import { BackArrow } from "../shared/BackArrow";
import { PrimaryButton, fieldStyle, SKY } from "../shared/ui";
import { searchCities, resolveTypedCity, toBirthLocation, prefetchCities, isNonLatin } from "../lib/cityLookup";

/* Three short steps, one question each: date, time, place. With
   `autoAdvance` (the signed-out first run) each step moves on by itself once
   its answer is in (a date filled in from empty, "I don't know", a city
   picked from the list); Continue stays for typed times and as a fallback.
   Settings keeps Continue as the only way on. With `askName` (adding a
   person in People) the first step also takes their name, so there's no
   separate name screen; onDone then gets (birthData, name).
   Produces the app's birthData shape:
   { birthDate:{year,month,day}, hasBirthTime, birthTime:{hour,minute,period}|null, birthLocation } */

const STEPS = ["date", "time", "place"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
// A year counts only once all four digits are in, so "2", "20" and "200" on
// the way to "2000" never take February 29 away.
const daysIn = (m, y) => (m ? new Date(Date.UTC(y >= 1000 ? y : 2000, m, 0)).getUTCDate() : 31);
// The local date, not UTC: a future date is refused in Honolulu and a baby
// born today is accepted late in the day east of UTC.
const localToday = () => {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}-${String(n.getDate()).padStart(2, "0")}`;
};

function toISODate(bd) {
  if (!bd?.birthDate) return "";
  const { year, month, day } = bd.birthDate;
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}
function toTimeValue(bd) {
  if (!bd?.hasBirthTime || !bd.birthTime) return "";
  let h = bd.birthTime.hour % 12;
  if (bd.birthTime.period === "PM") h += 12;
  return `${String(h).padStart(2, "0")}:${String(bd.birthTime.minute).padStart(2, "0")}`;
}

export function BirthDetails({ initial, draft = null, initialStep = 0, onDone, onBack, subject: subjectProp = null, submitLabel, autoAdvance = false, askName = false }) {
  const [name, setName] = useState("");
  // `subject` is someone else's name when adding a person to compare with.
  const subject = askName ? (name.trim() || "them") : subjectProp;
  const their = !subject ? "your" : subject === "them" ? "their" : `${subject}'s`;
  const T = subject ? {
    date: `When was ${subject} born?`, time: "What time?", place: `Where was ${subject} born?`,
    timeSub: `A birth time adds ${their} Rising sign and houses. If you don't know it, skip it and you'll still see Sun and Moon side by side.`,
    noTime: `I don't know ${their} birth time`,
    noTimeNote: "That's fine. The comparison still covers the Sun, Moon, and planets.",
    city: `${subject}'s birth city`,
  } : {
    date: "When were you born?", time: "What time?", place: "Where were you born?",
    timeSub: "A birth time adds your Rising sign and shows which areas of life each planet touches. It's on most birth certificates.",
    noTime: "I don't know my birth time",
    noTimeNote: "That's fine. You'll still get your Sun, Moon, and planets. You can add a time later.",
    city: "your birth city",
  };
  const [step, setStep] = useState(initialStep);
  const initISO = toISODate(initial);
  // `draft` is what was typed before Back to the landing, kept for the return.
  const d0 = !initial && draft ? draft : null;
  const [month, setMonth] = useState(initISO ? String(+initISO.slice(5, 7)) : d0?.month || "");
  const [day, setDay] = useState(initISO ? String(+initISO.slice(8, 10)) : d0?.day || "");
  const [year, setYear] = useState(initISO ? initISO.slice(0, 4) : d0?.year || "");
  const [time, setTime] = useState(d0 ? d0.time : toTimeValue(initial));
  const [noTime, setNoTime] = useState(initial ? !initial.hasBirthTime : !!d0?.noTime);
  const [place, setPlace] = useState(initial?.birthLocation || d0?.place || null);
  const [query, setQuery] = useState(initial?.birthLocation?.city || d0?.query || "");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState(null);
  const seq = useRef(0);
  const inputRef = useRef(null);
  const advance = useRef(null);
  // Only edits made on this step move it on, never values carried in, and a
  // date that came in complete is being edited, so it never jumps ahead.
  const touched = useRef(false);
  const dateWasEmpty = useRef(!initial?.birthDate);

  useEffect(() => { prefetchCities(); }, []);
  useEffect(() => {
    setError(null);
    touched.current = false;
    clearTimeout(advance.current);
    const t = setTimeout(() => inputRef.current?.focus({ preventScroll: true }), 350);
    return () => clearTimeout(t);
  }, [step]);

  const today = localToday();
  const date = month && day && year.length === 4 ? `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}` : "";
  const dateOk = (d) => {
    const [y, m, dd] = d.split("-").map(Number);
    return !!(y && m && dd && y >= 1900 && d <= today && dd <= daysIn(m, y));
  };

  // A day the month no longer has (February 29, then 2001) is cleared, and
  // the message says why, rather than the box quietly showing another day.
  useEffect(() => {
    const max = daysIn(+month, year.length === 4 ? +year : 0);
    if (!day || +day <= max) return;
    setDay("");
    const yearIn = year.length === 4;
    const which = +month === 2 && yearIn ? `February ${year}` : MONTHS[+month - 1];
    setError(`${which} has ${max} days${+month === 2 && !yearIn ? " at most" : ""}. Pick the day again.`);
  }, [month, year]); // eslint-disable-line

  const later = (fn) => { clearTimeout(advance.current); advance.current = setTimeout(fn, 450); };
  useEffect(() => () => clearTimeout(advance.current), []);
  useEffect(() => {
    if (!autoAdvance || !dateWasEmpty.current || STEPS[step] !== "date" || !touched.current || !date || (askName && !name.trim())) return;
    if (dateOk(date)) later(() => setStep(1));
    return () => clearTimeout(advance.current);
  }, [month, day, year]); // eslint-disable-line

  const next = (e) => {
    e?.preventDefault?.();
    setError(null);
    if (STEPS[step] === "date") {
      if (askName && !name.trim()) { setError("Add their name."); inputRef.current?.focus(); return; }
      if (year && year.length < 4) { setError("Enter all four digits of the year."); return; }
      if (!month || !day) { setError(month ? "Pick the day." : day ? "Pick the month." : "Pick the month and day."); return; }
      if (!year) { setError("Type the year."); return; }
      if (!dateOk(date)) { setError("Pick a birth date between 1900 and today."); return; }
    }
    if (STEPS[step] === "time" && !noTime && !time) { setError(`Add a time, or tap “${T.noTime}.”`); return; }
    if (STEPS[step] === "place") {
      const loc = (e && e.loc) || place;
      if (loc) { finish(loc); return; }
      if (query.trim().length < 2) { setError(`Type ${T.city}.`); return; }
      // A typed city without a pick only counts when it can mean one place
      // ("Zurich"). "Geneva" or "Saint Louis" shows the choices and asks.
      const n = ++seq.current;
      setSearching(true);
      resolveTypedCity(query, 5)
        .then((r) => {
          if (n !== seq.current) return;
          setSearching(false);
          if (r.city) {
            const loc = toBirthLocation(r.city);
            setPlace(loc); setQuery(loc.city); setResults([]);
            finish(loc);
            return;
          }
          setResults(r.choices);
          // With no choices, the no-match hint under the field says it all.
          if (r.choices.length) setError(r.choices.length > 1 ? "A few places match. Pick yours from the list." : `Pick ${T.city} from the list.`);
        })
        .catch(() => { if (n === seq.current) { setSearching(false); setError(`Pick ${T.city} from the list.`); } });
      return;
    }
    setStep(step + 1);
  };

  const finish = (loc) => {
    const [y, m, d] = date.split("-").map(Number);
    let birthTime = null;
    const hasBirthTime = !noTime && !!time;
    if (hasBirthTime) {
      const [hh, mm] = time.split(":").map(Number);
      birthTime = { hour: hh % 12 === 0 ? 12 : hh % 12, minute: mm, period: hh >= 12 ? "PM" : "AM" };
    }
    onDone({ birthDate: { year: y, month: m, day: d }, hasBirthTime, birthTime, birthLocation: loc }, askName ? name.trim() : undefined);
  };

  // Back also drops a city lookup still running, so it can't finish the step.
  const back = () => {
    ++seq.current; setSearching(false);
    return step === 0 ? onBack({ month, day, year, time, noTime, place, query }) : setStep(step - 1);
  };

  const search = (val) => {
    setQuery(val);
    setPlace(null);
    setError(null);
    if (val.trim().length < 2) { setResults([]); setSearching(false); return; }
    const n = ++seq.current;
    setSearching(true);
    searchCities(val, 5)
      .then((r) => { if (n === seq.current) { setResults(r); setSearching(false); } })
      .catch(() => { if (n === seq.current) { setResults([]); setSearching(false); } });
  };

  // Picking a city is the last answer, so it opens the chart straight away.
  const pick = (c) => {
    const loc = toBirthLocation(c);
    setPlace(loc);
    setQuery(loc.city);
    setResults([]);
    if (autoAdvance) next({ loc });
  };

  const kind = STEPS[step];
  const title = kind === "date" ? (askName ? "Who are you comparing with?" : T.date) : kind === "time" ? T.time : T.place;

  return (
    <div style={{
      minHeight: "100vh", background: PD.bg, position: "relative", overflow: "hidden",
      backgroundImage: `radial-gradient(ellipse at 30% 10%, ${SKY.a}22 0%, transparent 55%), radial-gradient(ellipse at 80% 90%, #1A284050 0%, transparent 50%)`,
    }}>
      <StarField seed={333} />
      <form onSubmit={next} noValidate style={{ position: "relative", zIndex: 1, maxWidth: 460, margin: "0 auto", padding: "12px 20px 40px", minHeight: "100vh", display: "flex", flexDirection: "column" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <BackArrow onClick={back} color={PD.sub} />
          <div role="progressbar" aria-label="Birth details" aria-valuemin={1} aria-valuemax={3} aria-valuenow={step + 1} aria-valuetext={`Step ${step + 1} of 3`} style={{ display: "flex", gap: 6 }}>
            {STEPS.map((s, i) => (
              <span key={s} aria-hidden="true" style={{
                width: i === step ? 22 : 8, height: 8, borderRadius: 4,
                background: i <= step ? SKY.a : PD.borderLit, transition: "all 0.35s ease",
                boxShadow: i === step ? `0 0 10px ${SKY.glow}` : "none",
              }} />
            ))}
          </div>
          <span style={{ width: 60 }} />
        </div>

        <div key={kind} className="tw-rise" style={{ marginTop: askName && kind === "date" ? "6vh" : "12vh", animation: "twRise 0.5s ease both" }}>
          <h1 style={{ ...HD, fontSize: "clamp(28px, 7vw, 36px)", color: PD.text, fontWeight: 500, margin: "0 0 10px", lineHeight: 1.15 }}>
            {title}
          </h1>

          {kind === "date" && (
            <>
              {askName && (
                <>
                  <p style={{ ...B, fontSize: 15, color: PD.sub, lineHeight: 1.6, margin: "0 0 20px" }}>
                    A friend, a sibling, a parent, a coworker. Their details stay on this device.
                  </p>
                  <input ref={inputRef} className="tw-field" value={name} maxLength={40} placeholder="Their first name" aria-label="Their name"
                    autoComplete="off" onChange={(e) => { setError(null); setName(e.target.value); }} style={fieldStyle} />
                  <p style={{ ...B, fontSize: 13, letterSpacing: "0.12em", textTransform: "uppercase", color: PD.sub, margin: "22px 0 0" }}>Their birthday</p>
                </>
              )}
              {/* Bases in em let the row wrap with large text instead of cutting off
                  "September" or the year. */}
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: askName ? 10 : 24 }}>
                <select ref={askName ? undefined : inputRef} className="tw-field" value={month} onChange={(e) => { touched.current = true; setError(null); setMonth(e.target.value); }} aria-label="Birth month"
                  style={{ ...fieldStyle, flex: "2.2 1 8em", color: month ? PD.text : PD.sub, appearance: "none", WebkitAppearance: "none" }}>
                  <option value="" disabled>Month</option>
                  {MONTHS.map((m, i) => <option key={m} value={String(i + 1)}>{m}</option>)}
                </select>
                <select className="tw-field" value={day} onChange={(e) => { touched.current = true; setError(null); setDay(e.target.value); }} aria-label="Birth day"
                  style={{ ...fieldStyle, flex: "1 1 3.6em", color: day ? PD.text : PD.sub, appearance: "none", WebkitAppearance: "none", textAlign: "center" }}>
                  <option value="" disabled>Day</option>
                  {Array.from({ length: daysIn(+month, year.length === 4 ? +year : 0) }, (_, i) => <option key={i} value={String(i + 1)}>{i + 1}</option>)}
                </select>
                <input className="tw-field" type="text" inputMode="numeric" pattern="[0-9]*" maxLength={4} placeholder="Year"
                  value={year} onChange={(e) => { touched.current = true; setError(null); setYear(e.target.value.replace(/\D/g, "")); }} aria-label="Birth year"
                  style={{ ...fieldStyle, flex: "1.4 1 4.6em", textAlign: "center" }} />
              </div>
            </>
          )}

          {kind === "time" && (
            <>
              <p style={{ ...B, fontSize: 15, color: PD.sub, lineHeight: 1.6, margin: "0 0 28px" }}>
                {T.timeSub}
              </p>
              <input ref={inputRef} type="time" className="tw-field" value={noTime ? "" : time} disabled={noTime}
                onChange={(e) => { clearTimeout(advance.current); setTime(e.target.value); setNoTime(false); }} aria-label="Birth time"
                style={{ ...fieldStyle, opacity: noTime ? 0.4 : 1 }} />
              <button type="button" role="checkbox" aria-checked={noTime} onClick={() => {
                const on = !noTime;
                setNoTime(on); setError(null);
                if (on && autoAdvance) later(() => setStep(2)); else clearTimeout(advance.current);
              }} style={{
                ...B, marginTop: 14, display: "flex", alignItems: "center", gap: 10, width: "100%",
                background: noTime ? "rgba(126,142,194,0.14)" : "transparent", color: PD.text, fontSize: 15,
                border: `1px solid ${noTime ? SKY.a : PD.borderLit}`, borderRadius: 14, padding: "14px 16px", minHeight: 52, cursor: "pointer", textAlign: "left",
              }}>
                <span aria-hidden style={{
                  width: 20, height: 20, borderRadius: 6, border: `1.5px solid ${noTime ? SKY.a : PD.sub}`,
                  background: noTime ? SKY.a : "transparent", display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                }}>
                  {noTime && <svg width="12" height="12" viewBox="0 0 12 12"><path d="M2.5 6.2l2.2 2.2 4.8-5" stroke={SKY.ink} strokeWidth="1.8" fill="none" strokeLinecap="round" /></svg>}
                </span>
                {T.noTime}
              </button>
              {noTime && (
                <p style={{ ...B, fontSize: 13, color: PD.sub, lineHeight: 1.6, marginTop: 12 }}>
                  {T.noTimeNote}
                </p>
              )}
            </>
          )}

          {kind === "place" && (
            <>
              <p style={{ ...B, fontSize: 15, color: PD.sub, lineHeight: 1.6, margin: "0 0 28px" }}>
                The sky looks different from every city.
              </p>
              {/* Above the field here, since the list below would cover it. */}
              {error && <p role="alert" style={{ ...B, fontSize: 14, color: "#E39AAE", margin: "-14px 0 12px" }}>{error}</p>}
              <div style={{ position: "relative" }}>
                <input ref={inputRef} type="text" className="tw-field" value={query} placeholder="Start typing a city"
                  autoComplete="off" onChange={(e) => search(e.target.value)} aria-label="Birth city" style={fieldStyle} />
                {results.length > 0 && (
                  <div role="listbox" style={{
                    position: "absolute", top: "calc(100% + 6px)", left: 0, right: 0, zIndex: 20, background: PD.bgCard,
                    border: `1px solid ${PD.borderLit}`, borderRadius: 14, overflow: "hidden", boxShadow: "0 12px 32px rgba(0,0,0,0.45)",
                  }}>
                    {results.map((c, i) => (
                      <button key={`${c.name}-${c.lat}-${i}`} type="button" role="option" aria-selected="false"
                        onClick={() => pick(c)} style={{
                          ...B, display: "block", width: "100%", textAlign: "left", background: "none", border: "none",
                          borderTop: i ? `1px solid ${PD.border}` : "none", padding: "13px 16px", minHeight: 48, cursor: "pointer", color: PD.text, fontSize: 15,
                        }}>
                        {c.name}{c.alias ? ` (${c.alias})` : ""}<span style={{ color: PD.sub, fontSize: 13, marginLeft: 8 }}>{c.label.replace(c.name + ", ", "")}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {searching && !results.length && <p style={{ ...B, fontSize: 13, color: PD.sub, marginTop: 10 }}>Searching…</p>}
              {!searching && !place && !results.length && query.trim().length >= 2 && (
                <p style={{ ...B, fontSize: 13, color: PD.sub, marginTop: 10 }}>
                  {isNonLatin(query) ? "No match yet. Try the English spelling." : "No match yet. Try a nearby larger city."}
                </p>
              )}
              {place && <p style={{ ...B, fontSize: 13, color: "#7FB894", marginTop: 10 }}>✓ {place.city}</p>}
            </>
          )}

          {error && kind !== "place" && <p role="alert" style={{ ...B, fontSize: 14, color: "#E39AAE", marginTop: 16 }}>{error}</p>}
        </div>

        <div style={{ marginTop: "auto", paddingTop: 40, display: "flex", flexDirection: "column", alignItems: "stretch", gap: 6 }}>
          <PrimaryButton type="submit" style={{ width: "100%" }}>
            {kind === "place" ? (submitLabel || "Show me my chart") : "Continue"}
          </PrimaryButton>
        </div>
      </form>
    </div>
  );
}
